import React from 'react';
import { PieChart, Target, TrendingUp, Shield, BarChart3, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const FinancialPlanningPage = () => {
  const services = [
    {
      icon: Target,
      title: 'Goal Setting',
      description: 'Work with our experts to define clear financial goals and create a roadmap to achieve them, whether it\'s buying a home, funding education, or building wealth.'
    },
    {
      icon: PieChart,
      title: 'Budget Planning',
      description: 'Develop comprehensive budgets and spending plans that align with your income, expenses, and financial objectives for better money management.'
    },
    {
      icon: TrendingUp,
      title: 'Wealth Building',
      description: 'Strategies for building and growing your wealth through smart investment decisions, savings plans, and long-term financial planning.'
    },
    {
      icon: Shield,
      title: 'Risk Assessment',
      description: 'Comprehensive risk analysis to protect your financial future and ensure you have adequate insurance and emergency funds in place.'
    },
    {
      icon: BarChart3,
      title: 'Retirement Planning',
      description: 'Plan for a secure retirement with strategies for savings, investments, and income planning to ensure financial independence in your golden years.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/80 to-navy-800/60"></div>
        <div className="absolute inset-0">
          <img
            src="/images/analysis-1841158_1280.jpg"
            alt="Financial Planning"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold font-serif mb-4">Financial Planning</h1>
          <p className="text-xl md:text-2xl text-gray-200">
            Home / <span className="text-gold-400">Services</span> / <span className="text-gold-400">Financial Planning</span>
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-6">
              Comprehensive <span className="text-gold-600">Financial Planning Services</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Create a comprehensive financial roadmap with our personalized planning services. Our experts help you develop strategies for wealth building and management that align with your unique financial situation and goals.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Whether you're just starting your financial journey or looking to optimize your existing plan, we provide tailored advice and strategies to help you achieve financial security, build wealth, and plan for the future with confidence.
              </p>
            </div>
            
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src="/images/analysis-1841158_1280.jpg"
                  alt="Financial Planning Strategy"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/50 to-transparent"></div>
              </div>
            </div>
          </div>

          {/* Services Section */}
          <div className="mb-20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div key={index} className="group bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                    <div className="inline-flex p-4 bg-gradient-to-r from-gold-500 to-gold-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-navy-900 mb-4">
                      {service.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl md:text-4xl font-bold font-serif mb-6">
              Ready to Plan Your Financial Future?
            </h3>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Let our financial planning experts help you create a roadmap to achieve your goals. 
              Start building your financial future with personalized planning today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-gold-600 hover:bg-gold-700 text-white font-bold rounded-full transition-colors duration-300"
              >
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link 
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-navy-900 font-bold rounded-full transition-all duration-300"
              >
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinancialPlanningPage;

